import { Link, useLocation, useNavigate } from 'react-router-dom';
import useResponsiveSidebar from '../hooks/useResponsiveSidebar';
import { ResponsiveSidebar } from './ResponsiveSidebar';
import { useAuth } from '../contexts/AuthContext';
import { getDivisions } from '../utils/divisions';
import { Wrench, Truck, LogOut, Home as HomeIcon, UserPlus, CreditCard, Settings as SettingsIcon, PanelLeftOpen, PanelLeftClose } from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';

export default function HomeLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const sidebar = useResponsiveSidebar('homeSidebarCollapsed');

  const perms = user?.effective_permissions || [];
  const divisions = getDivisions(user);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const divisionItems = [
    { path: '/dashboard', icon: Wrench, label: 'Shop', show: divisions.includes('shop'), accent: 'text-orange-400' },
    { path: '/field/calendar', icon: Truck, label: 'Field', show: divisions.includes('field'), accent: 'text-sky-400' },
  ].filter((i) => i.show);

  const orgItems = [
    { path: '/users', icon: UserPlus, label: 'Users', perm: 'users' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings', perm: 'settings' },
    { path: '/billing', icon: CreditCard, label: 'Billing', perm: 'billing' },
  ].filter((i) => perms.includes(i.perm));

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-slate-50" style={{ minHeight: '100dvh' }}>
      <ResponsiveSidebar sidebar={sidebar} testid="home-sidebar">
        <div className="p-6 border-b border-slate-800 shrink-0">
          <h1 className="text-2xl font-black tracking-tight">ShopManager<span className="text-orange-400">247</span></h1>
          <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Operations Hub</p>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="home-sidebar-nav">
          <Link
            to="/home"
            data-testid="homelayout-nav-home"
            className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
              isActive('/home') ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
          >
            <HomeIcon size={20} />
            <span className="font-medium">Home</span>
          </Link>

          {divisionItems.length > 0 && (
            <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Divisions</p>
          )}
          {divisionItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`homelayout-nav-${item.label.toLowerCase()}`}
                className="flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <Icon size={20} className={item.accent} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}

          {orgItems.length > 0 && (
            <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Organization</p>
          )}
          {orgItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`homelayout-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive(item.path) ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div
          className="p-4 border-t border-slate-800 shrink-0"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="px-4 py-2 mb-2">
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-xs text-slate-400 uppercase tracking-wider">{user?.role?.replace(/_/g, ' ')}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="homelayout-logout-button"
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </Button>
        </div>
      </ResponsiveSidebar>

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="h-16 bg-white border-b border-slate-200 flex items-center justify-between gap-3 px-4 md:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={sidebar.toggleCollapsed} aria-label={sidebar.collapsed ? 'Show navigation' : 'Hide navigation'} aria-expanded={!sidebar.collapsed} data-testid="home-sidebar-toggle">
              {sidebar.collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </Button>
            <h2 className="text-lg md:text-xl font-bold text-slate-900 truncate" data-testid="home-layout-title">{title || 'Operations Hub'}</h2>
          </div>
          <NotificationBell />
        </div>
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
